import { useMemo, useRef, useState, type FocusEvent } from "react";
import {
  appendPercentagePointStep,
  parseExpression,
  parsePercentageExpression,
} from "../lib/expression";

interface ExpressionInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  percentage?: boolean;
  placeholder?: string;
  hint?: string;
}

const QUICK_STEPS = [
  { label: "+5%", value: 5 },
  { label: "−5%", value: -5 },
  { label: "+1%", value: 1 },
  { label: "−1%", value: -1 },
] as const;

const BASE_OPERATOR_KEYS = [
  { label: "+", value: "+" },
  { label: "−", value: "-" },
  { label: "×", value: "*" },
  { label: "÷", value: "/" },
  { label: "(", value: "(" },
  { label: ")", value: ")" },
] as const;

const STANDARD_OPERATOR_KEYS = [
  ...BASE_OPERATOR_KEYS,
  { label: "%", value: "%" },
] as const;

function formatValue(value: number, percentage: boolean): string {
  const displayed = percentage ? value * 100 : value;
  const formatted = new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: 8,
    useGrouping: false,
  }).format(displayed);
  return percentage ? `${formatted}%` : formatted;
}

export function ExpressionInput({
  label,
  value,
  onChange,
  percentage = false,
  placeholder,
  hint,
}: ExpressionInputProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const result = useMemo(
    () => (percentage ? parsePercentageExpression(value) : parseExpression(value)),
    [percentage, value],
  );
  const showError = value.trim() !== "" && !result.ok;
  const operatorKeys = percentage ? BASE_OPERATOR_KEYS : STANDARD_OPERATOR_KEYS;
  const resolvedPlaceholder =
    placeholder ?? (percentage ? "例如 20 或 20+15" : "输入数值或算式");

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setFocused(false);
    }
  };

  const insertOperator = (operator: string) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? start;
    const nextValue = `${value.slice(0, start)}${operator}${value.slice(end)}`;
    const nextPosition = start + operator.length;
    onChange(nextValue);
    window.requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(nextPosition, nextPosition);
    });
  };

  return (
    <div
      className={`expression-field${showError ? " has-error" : ""}`}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={handleBlur}
    >
      <label>
        <span className="field-label">{label}</span>
        <span className="input-shell">
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            spellCheck={false}
            value={value}
            placeholder={resolvedPlaceholder}
            aria-invalid={showError}
            onChange={(event) => onChange(event.target.value)}
          />
        </span>
      </label>

      <div className={`field-evaluation${showError ? " error" : ""}`} aria-live="polite">
        {result.ok && result.value !== null
          ? `= ${formatValue(result.value, percentage)}`
          : showError
            ? result.error
            : hint ?? "支持英文符号 + - * / ( ) %"}
      </div>

      {focused && (
        <div
          className={`operator-keys${percentage ? " percentage-operators" : ""}`}
          aria-label={`${label}算式符号`}
        >
          {operatorKeys.map((operator) => (
            <button
              key={operator.label}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertOperator(operator.value)}
              aria-label={`插入 ${operator.label}`}
            >
              {operator.label}
            </button>
          ))}
        </div>
      )}

      {percentage && focused && (
        <div className="quick-steps" aria-label={`${label}快捷调整`}>
          {QUICK_STEPS.map((step) => (
            <button
              key={step.label}
              type="button"
              disabled={!result.ok}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChange(appendPercentagePointStep(value, step.value))}
            >
              {step.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
